<template>
  <div class="ov-page">
    <!-- ===== EN-TÊTE : ACCUEIL + SÉLECTION COMPAGNIE / GARE ===== -->
    <div class="adm-head">
      <div>
        <div class="adm-head__date">{{ currentDate }}</div>
        <h1 class="adm-head__title">{{ $t('dashboard.welcome') }}, {{ $store.state.userAuthentified.prenoms || $store.state.userAuthentified.nom }}</h1>
        <div v-if="$store.state.userAuthentified.lastConnection" class="adm-head__sub">
          {{ $t('dashboard.lastConnection') }} {{ $store.state.userAuthentified.lastConnection }}
        </div>
      </div>

      <div class="adm-filters">
        <select v-model="selectedCompany" class="ov-select" :aria-label="$t('dashboard.company')" @change="onCompanyChange">
          <option :value="null" disabled>{{ $t('dashboard.selectCompany') }}</option>
          <option v-for="c in companyList" :key="c.raisonSociale" :value="c.raisonSociale">{{ c.raisonSociale }}</option>
        </select>
        <select
          v-model="selectedStation"
          class="ov-select"
          :aria-label="$t('dashboard.station')"
          :disabled="!selectedCompany || loadingStations"
          @change="onStationChange"
        >
          <option :value="null">{{ selectedCompany ? 'Toutes les gares' : $t('dashboard.selectStation') }}</option>
          <option v-for="s in stationList" :key="s.designation" :value="s.designation">{{ s.designation }}</option>
        </select>
      </div>
    </div>

    <!-- ===== AUCUNE COMPAGNIE SÉLECTIONNÉE ===== -->
    <div v-if="!selectedCompany" class="ov-card">
      <div class="ov-empty adm-start">
        <v-icon large color="#94a3b8">mdi-domain</v-icon>
        <p>Sélectionnez une compagnie pour afficher ses statistiques de réservation et ses offres de voyage.</p>
      </div>
    </div>

    <template v-else>
      <!-- ===== RÉSERVATIONS ===== -->
      <section class="adm-section">
        <div class="adm-section__head adm-section__head--filters">
          <div class="adm-section__titles">
            <h2 class="adm-section__title">{{ $t('dashboard.reservationStatistics') }}</h2>
            <span class="ov-badge ov-badge--navy ov-badge--plain">{{ scopeLabel }}</span>
          </div>
          <div class="adm-filters">
            <select v-model="dateSelected" class="ov-select" :aria-label="$t('dashboard.byDate')">
              <option :value="null" disabled>{{ $t('dashboard.byDate') }}</option>
              <option v-for="d in dateFilterList" :key="d.value" :value="d.value">{{ d.title }}</option>
            </select>
            <template v-if="dateSelected === 'period'">
              <input v-model="dateDebut" type="date" class="ov-input" :aria-label="$t('dashboard.startDate')" :title="$t('dashboard.startDate')" />
              <input v-model="dateFin" type="date" class="ov-input" :aria-label="$t('dashboard.endDate')" :title="$t('dashboard.endDate')" />
            </template>
            <input
              v-if="dateSelected === 'date'"
              v-model="datePrecise"
              type="date"
              class="ov-input"
              :aria-label="$t('dashboard.preciseDate')"
              :title="$t('dashboard.preciseDate')"
            />
            <button type="button" class="ov-btn ov-btn--primary" :disabled="!canApplyFilter || filtering" @click="applyFilter">
              <v-progress-circular v-if="filtering" indeterminate size="16" width="2"></v-progress-circular>
              <v-icon v-else small>mdi-magnify</v-icon>
              {{ $t('dashboard.apply') }}
            </button>
          </div>
        </div>

        <div v-for="block in blocks" :key="block.key" class="adm-block">
          <div class="adm-block__head">
            <h3 class="adm-block__title">{{ block.title }}</h3>
            <span class="ov-badge" :class="block.badgeClass">{{ block.badge }}</span>
            <button v-if="block.closable" type="button" class="ov-icon-btn adm-block__close" title="Retirer le filtre" @click="clearFilter">
              <v-icon small>mdi-close</v-icon>
            </button>
          </div>

          <!-- Une gare : bloc partagé (graphiques par programme construits à partir des statistiques brutes) -->
          <StatistiqueReservationParGareTransport
            v-if="isStationScope"
            :chart-id="'adm-' + block.key"
            :dataStatistiqueGareTransport="block.stats"
            :data-statistic-bag-station="block.bags"
          ></StatistiqueReservationParGareTransport>

          <!-- Toute la compagnie : statistiques par offre de voyage -->
          <template v-else>
            <div class="ov-kpis">
              <div class="ov-kpi ov-kpi--primary">
                <div class="ov-kpi__label">{{ $t('dashboard.revenue') }}</div>
                <div class="ov-kpi__value">{{ formatNumber(block.stats && block.stats.chiffreAffaires) }}<span>FCFA</span></div>
              </div>
              <div class="ov-kpi">
                <div class="ov-kpi__label">{{ $t('dashboard.totalReservations') }}</div>
                <div class="ov-kpi__value">{{ formatNumber(block.stats && block.stats.nombreTotalReservationBilletVoyage) }}</div>
              </div>
              <div class="ov-kpi">
                <div class="ov-kpi__label">{{ $t('dashboard.luggageRevenue') }}</div>
                <div class="ov-kpi__value">{{ formatNumber(block.stats && block.stats.chiffreAffairesBagages) }}<span>FCFA</span></div>
              </div>
            </div>

            <div class="ov-charts">
              <div class="ov-card">
                <div class="ov-card__head">
                  <div>
                    <div class="ov-card__title">Chiffre d'affaires par offre</div>
                    <div class="ov-card__sub">{{ block.revenueChart.datasets.length > 1 ? 'Réservations et bagages, en FCFA' : 'Réservations, en FCFA' }}</div>
                  </div>
                </div>
                <div class="ov-card__body">
                  <Bar
                    v-if="hasData(block.revenueChart)"
                    :chart-id="'adm-' + block.key + '-revenue'"
                    :chart-data="block.revenueChart"
                    :chart-options="block.revenueChart.datasets.length > 1 ? revenueOptions : revenueOptionsSingle"
                    :styles="chartStyles"
                  />
                  <div v-else class="ov-empty ov-chart-empty">Aucune donnée pour cette période.</div>
                </div>
              </div>
              <div class="ov-card">
                <div class="ov-card__head">
                  <div>
                    <div class="ov-card__title">Réservations par offre</div>
                    <div class="ov-card__sub">Classement sur la période</div>
                  </div>
                </div>
                <div class="ov-card__body">
                  <Bar
                    v-if="hasData(block.rankingChart)"
                    :chart-id="'adm-' + block.key + '-ranking'"
                    :chart-data="block.rankingChart"
                    :chart-options="rankingOptions"
                    :styles="rankingHeight(block.rankingChart)"
                  />
                  <div v-else class="ov-empty ov-chart-empty">Aucune réservation pour cette période.</div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </section>

      <!-- ===== OFFRES DE VOYAGE ===== -->
      <section class="adm-section">
        <div class="adm-section__head">
          <div class="adm-section__titles">
            <h2 class="adm-section__title">{{ $t('dashboard.travelOffersStatistics') }}</h2>
            <span class="ov-badge ov-badge--navy ov-badge--plain">{{ selectedCompany }}</span>
          </div>
        </div>

        <div class="ov-card">
          <div class="ov-card__head">
            <div class="ov-card__title">
              {{ $t('dashboard.travelOffersList') }}
              <span class="ov-count">{{ filteredOffers.length }}</span>
            </div>
            <div class="ov-filters">
              <div class="adm-seg" role="group" aria-label="Filtrer par statut">
                <button type="button" class="adm-seg__opt" :class="{ 'adm-seg__opt--active': offerStatus === null }" @click="offerStatus = null">Toutes</button>
                <button type="button" class="adm-seg__opt" :class="{ 'adm-seg__opt--active': offerStatus === true }" @click="offerStatus = true">Actives ({{ activeOffersCount }})</button>
                <button type="button" class="adm-seg__opt" :class="{ 'adm-seg__opt--active': offerStatus === false }" @click="offerStatus = false">Inactives</button>
              </div>
              <div class="ov-search">
                <v-icon small>mdi-magnify</v-icon>
                <input v-model="offerSearch" type="text" class="ov-input" placeholder="Rechercher une offre..." />
              </div>
            </div>
          </div>

          <v-data-table
            class="ov-table"
            :headers="offerHeaders"
            :items="filteredOffers"
            :loading="loadingOffers"
            :search="offerSearch"
            loader-height="2"
            :items-per-page="5"
            no-data-text="Aucune offre de voyage"
          >
            <template v-slot:[`item.designation`]="{ item }">
              <span class="adm-offer-name" :class="{ 'adm-offer-name--selected': selectedOfferDesignation === item.designation }">
                {{ item.designation }}
              </span>
            </template>
            <template v-slot:[`item.isActif`]="{ item }">
              <span v-if="item.isActif" class="ov-badge ov-badge--success">{{ $t('dashboard.activeStatus') }}</span>
              <span v-else class="ov-badge ov-badge--neutral">{{ $t('dashboard.inactiveStatus') }}</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <div class="ov-row-actions adm-actions">
                <button
                  type="button"
                  class="ov-btn ov-btn--secondary ov-btn--sm"
                  :title="$t('dashboard.moreDetails')"
                  :disabled="loadingOfferStats"
                  @click="getOfferStatistics(item.designation)"
                >
                  <v-icon small>mdi-chart-bar</v-icon>
                  Statistiques
                </button>
              </div>
            </template>
          </v-data-table>
        </div>

        <!-- Détail de l'offre sélectionnée (année en cours) -->
        <div v-if="offerStats" class="adm-offer">
          <div class="adm-offer__head">
            <div>
              <div class="ov-card__kicker">Offre sélectionnée · {{ $t('dashboard.currentYear') }}</div>
              <div class="adm-offer__title">{{ offerStats.offreVoyageDesignation || selectedOfferDesignation }}</div>
            </div>
            <button type="button" class="ov-icon-btn" title="Fermer" @click="clearOfferSelection">
              <v-icon small>mdi-close</v-icon>
            </button>
          </div>

          <div class="ov-kpis">
            <div class="ov-kpi ov-kpi--primary">
              <div class="ov-kpi__label">{{ $t('dashboard.revenue') }}</div>
              <div class="ov-kpi__value">{{ formatNumber(offerStats.chiffreAffairesReservation) }}<span>FCFA</span></div>
            </div>
            <div class="ov-kpi">
              <div class="ov-kpi__label">{{ $t('dashboard.totalReservations') }}</div>
              <div class="ov-kpi__value">{{ formatNumber(offerStats.nombreReservationBilletVoyage) }}</div>
            </div>
            <div class="ov-kpi">
              <div class="ov-kpi__label">{{ $t('dashboard.luggageRevenue') }}</div>
              <div class="ov-kpi__value">{{ formatNumber(offerStats.chiffreAffairesBagage) }}<span>FCFA</span></div>
            </div>
          </div>

          <div class="ov-charts">
            <div class="ov-card">
              <div class="ov-card__head">
                <div>
                  <div class="ov-card__title">Chiffre d'affaires par programme</div>
                  <div class="ov-card__sub">{{ programRevenueChart.datasets.length > 1 ? 'Réservations et bagages, en FCFA' : 'Réservations, en FCFA' }}</div>
                </div>
              </div>
              <div class="ov-card__body">
                <Bar
                  v-if="hasData(programRevenueChart)"
                  chart-id="adm-program-revenue"
                  :chart-data="programRevenueChart"
                  :chart-options="programRevenueChart.datasets.length > 1 ? revenueOptions : revenueOptionsSingle"
                  :styles="chartStyles"
                />
                <div v-else class="ov-empty ov-chart-empty">Aucune donnée pour cette offre cette année.</div>
              </div>
            </div>
            <div class="ov-card">
              <div class="ov-card__head">
                <div>
                  <div class="ov-card__title">Réservations par programme</div>
                  <div class="ov-card__sub">Classement de l'année</div>
                </div>
              </div>
              <div class="ov-card__body">
                <Bar
                  v-if="hasData(programRankingChart)"
                  chart-id="adm-program-ranking"
                  :chart-data="programRankingChart"
                  :chart-options="rankingOptions"
                  :styles="rankingHeight(programRankingChart)"
                />
                <div v-else class="ov-empty ov-chart-empty">Aucune réservation pour cette offre.</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
  </div>
</template>

<script>
import axios from "axios";
import { Bar } from "vue-chartjs/legacy";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  LinearScale,
  CategoryScale,
} from "chart.js";
import {
  API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
  API_RECUPERER_LISTE_GARES_PAR_COMPAGNIE,
  API_STATISTIQUE_RESERVATIONS,
  API_STATISTIQUE_BAGAGES,
  API_STATISTIQUE_OFFRE_VOYAGE_RESERVATION,
  HEADERS,
} from "../globalConfig/globalConstConfig";
import offerService from "@/services/offer.service";
import StatistiqueReservationParGareTransport from "@/modules/statistiques/traders/views/StatistiqueReservationParGareTransport.vue";
import {
  SERIES_RESERVATION,
  SERIES_LUGGAGE,
  CHART_STYLES,
  chartOptions,
  formatNumber,
  buildGroupedBars,
  buildRanking,
  hasData,
  rankingHeight,
} from "@/modules/statistiques/chartTheme";
import { showErrorMessage } from "@/modules/messages/messageProcess";

ChartJS.register(Title, Tooltip, Legend, BarElement, LinearScale, CategoryScale);

// Premier objet { libellé: valeur } non vide parmi les clés proposées
const firstMap = (source, keys) => {
  if (!source) return null;
  const key = keys.find((k) => source[k] && Object.keys(source[k]).length);
  return key ? source[key] : null;
};

export default {
  name: "DashboardMainAdmin",
  components: {
    StatistiqueReservationParGareTransport,
    Bar,
  },

  data() {
    return {
      errorMsg: null,
      successMsg: null,

      // Sélection compagnie -> gare (la gare est optionnelle)
      companyList: [],
      stationList: [],
      loadingStations: false,
      selectedCompany: null,
      selectedStation: null,

      // Filtre de date (appliqué uniquement via le bouton « Appliquer »)
      dateSelected: null,
      dateDebut: null,
      dateFin: null,
      datePrecise: null,
      filtering: false,
      appliedFilter: null,
      filteredStats: null,
      filteredBags: null,

      dailyStats: null,
      dailyBags: null,
      annualStats: null,
      annualBags: null,

      // Jetons pour ignorer les réponses arrivées après un changement de sélection
      overviewRequest: 0,
      filterRequest: 0,

      offers: [],
      loadingOffers: false,
      offerSearch: "",
      offerStatus: null,
      selectedOfferDesignation: null,
      offerStats: null,
      loadingOfferStats: false,
    };
  },

  computed: {
    currentDate() {
      return new Date().toLocaleDateString("fr-FR", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      });
    },

    dateFilterList() {
      return [
        { title: this.$t("dashboard.periodic"), value: "period" },
        { title: this.$t("dashboard.specificDate"), value: "date" },
      ];
    },

    offerHeaders() {
      return [
        { text: this.$t("dashboard.designation"), value: "designation" },
        { text: this.$t("dashboard.offerType"), value: "typeOffreVoyageDesignation" },
        { text: this.$t("dashboard.departureCity"), value: "villeDepartDesignation" },
        { text: this.$t("dashboard.arrivalCity"), value: "villeDestinationDesignation" },
        { text: this.$t("dashboard.active"), value: "isActif" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ];
    },

    isStationScope() {
      return !!this.selectedStation;
    },

    scopeLabel() {
      return this.isStationScope
        ? `${this.$t("dashboard.station")} · ${this.selectedStation}`
        : `${this.$t("dashboard.company")} · ${this.selectedCompany}`;
    },

    canApplyFilter() {
      if (this.dateSelected === "period") return !!(this.dateDebut && this.dateFin);
      if (this.dateSelected === "date") return !!this.datePrecise;
      return false;
    },

    filterLabel() {
      const f = this.appliedFilter;
      if (!f) return "";
      return f.type === "period"
        ? `Du ${this.formatDate(f.dateDebut)} au ${this.formatDate(f.dateFin)}`
        : `Le ${this.formatDate(f.datePrecise)}`;
    },

    // Blocs affichés : période filtrée (si appliquée), aujourd'hui, année en cours
    blocks() {
      const blocks = [];
      if (this.appliedFilter) {
        blocks.push({
          key: "filtre",
          title: this.$t("dashboard.filteredStats"),
          badge: this.filterLabel,
          badgeClass: "ov-badge--navy ov-badge--plain",
          closable: true,
          stats: this.filteredStats,
          bags: this.filteredBags,
        });
      }
      blocks.push(
        {
          key: "jour",
          title: this.$t("dashboard.today"),
          badge: new Date().toLocaleDateString("fr-FR"),
          badgeClass: "ov-badge--success",
          stats: this.dailyStats,
          bags: this.dailyBags,
        },
        {
          key: "annee",
          title: this.$t("dashboard.currentYear"),
          badge: String(new Date().getFullYear()),
          badgeClass: "ov-badge--navy ov-badge--plain",
          stats: this.annualStats,
          bags: this.annualBags,
        }
      );
      // Graphiques par offre (périmètre compagnie uniquement ; la gare a son bloc partagé)
      return blocks.map((block) => ({
        ...block,
        revenueChart: this.revenueChart(block.stats, ["chiffreAffairesParOffreVoyage"], ["chiffreAffairesBagagesParOffreVoyage"]),
        rankingChart: this.rankingChart(block.stats, ["nombreReservationBilletVoyageParOffreVoyage"]),
      }));
    },

    activeOffersCount() {
      return this.offers.filter((o) => o.isActif).length;
    },

    filteredOffers() {
      if (this.offerStatus === null) return this.offers;
      return this.offers.filter((o) => !!o.isActif === this.offerStatus);
    },

    programRevenueChart() {
      return this.revenueChart(
        this.offerStats,
        ["chiffreAffairesReservationParProgramme", "chiffreAffairesParProgramme"],
        ["chiffreAffairesBagageParProgramme", "chiffreAffairesBagagesParProgramme"]
      );
    },

    programRankingChart() {
      return this.rankingChart(this.offerStats, ["nombreReservationBilletVoyageParProgramme"]);
    },

    revenueOptions() {
      return chartOptions({ currency: true, legend: true });
    },

    revenueOptionsSingle() {
      return chartOptions({ currency: true, legend: false });
    },

    rankingOptions() {
      return chartOptions({ currency: false, legend: false, horizontal: true });
    },

    chartStyles() {
      return CHART_STYLES;
    },
  },

  methods: {
    formatNumber,
    hasData,
    rankingHeight,

    // BARRES GROUPÉES : CA RÉSERVATIONS + CA BAGAGES ALIGNÉS SUR LES MÊMES LIBELLÉS
    revenueChart(stats, reservationKeys, luggageKeys) {
      return buildGroupedBars([
        { label: this.$t("dashboard.revenue"), map: firstMap(stats, reservationKeys), color: SERIES_RESERVATION },
        { label: this.$t("dashboard.luggageRevenue"), map: firstMap(stats, luggageKeys), color: SERIES_LUGGAGE },
      ]);
    },

    // CLASSEMENT HORIZONTAL DU NOMBRE DE RÉSERVATIONS
    rankingChart(stats, countKeys) {
      return buildRanking(firstMap(stats, countKeys), this.$t("dashboard.totalReservations"));
    },

    showError(message) {
      this.errorMsg = message;
      showErrorMessage();
    },

    // Appel POST commun : renvoie `item` (ou `items`) si succès, sinon affiche l'erreur
    async post(url, payload, field = "item") {
      try {
        const response = await axios.post(url, payload, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        });
        if (response.status == 200 && response.data.status.code == 800) {
          return response.data[field];
        }
        this.showError(response.data && response.data.status ? response.data.status.message : this.$t("dashboard.error"));
      } catch (e) {
        this.showError((e && e.message) || "Erreur réseau lors du chargement des données.");
      }
      return null;
    },

    // Corps de requête : compagnie ou gare sélectionnée, période éventuelle
    buildPayload({ dateGiven = null, dateDebut = null, dateFin = null } = {}) {
      const gare = this.selectedStation || null;
      return {
        data: {
          raisonSociale: gare ? null : this.selectedCompany,
          gareDesignation: gare,
          gareTransportDesignation: gare,
          dateGiven,
          period: { dateDebut, dateFin },
        },
      };
    },

    // RÉSERVATIONS (+ BAGAGES POUR UNE GARE) D'UNE PÉRIODE : "jour", "annuel", "period", "date".
    // L'API bagages n'existe qu'au niveau gare : pour la compagnie, le CA bagages vient des réservations.
    fetchPeriod(period, payload) {
      const section = this.isStationScope ? "gare" : "compagnie";
      return Promise.all([
        this.post(API_STATISTIQUE_RESERVATIONS(period, section), payload),
        this.isStationScope ? this.post(API_STATISTIQUE_BAGAGES(period), payload) : Promise.resolve(null),
      ]);
    },

    // LISTE DES COMPAGNIES VALIDÉES
    async loadCompanies() {
      const items = await this.post(API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES, { index: 0, size: 100 }, "items");
      this.companyList = items || [];
    },

    // GARES DE LA COMPAGNIE SÉLECTIONNÉE
    async loadStations() {
      const company = this.selectedCompany;
      this.loadingStations = true;
      const items = await this.post(API_RECUPERER_LISTE_GARES_PAR_COMPAGNIE, {
        data: { compagnieTransportRaisonSociale: company },
      }, "items");
      if (company !== this.selectedCompany) return;
      this.stationList = items || [];
      this.loadingStations = false;
    },

    // OFFRES DE VOYAGE DE LA COMPAGNIE SÉLECTIONNÉE
    async loadOffers() {
      const company = this.selectedCompany;
      this.loadingOffers = true;
      try {
        const offers = await offerService.getOffersByCompany(company);
        if (company === this.selectedCompany) this.offers = offers || [];
      } catch (e) {
        this.showError((e && e.message) || "Erreur lors du chargement des offres de voyage.");
      } finally {
        this.loadingOffers = false;
      }
    },

    // STATISTIQUES DU JOUR ET DE L'ANNÉE POUR LE PÉRIMÈTRE COURANT
    async loadOverview() {
      const id = ++this.overviewRequest;
      this.dailyStats = this.dailyBags = this.annualStats = this.annualBags = null;
      const payload = this.buildPayload();
      const [[dailyStats, dailyBags], [annualStats, annualBags]] = await Promise.all([
        this.fetchPeriod("jour", payload),
        this.fetchPeriod("annuel", payload),
      ]);
      if (id !== this.overviewRequest) return;
      Object.assign(this, { dailyStats, dailyBags, annualStats, annualBags });
    },

    onCompanyChange() {
      this.selectedStation = null;
      this.stationList = [];
      this.offers = [];
      this.clearOfferSelection();
      this.loadStations();
      this.loadOffers();
      this.onScopeChange();
    },

    onStationChange() {
      this.onScopeChange();
    },

    // Le périmètre a changé : on recharge jour / année et le filtre déjà appliqué
    onScopeChange() {
      this.loadOverview();
      if (this.appliedFilter) this.runFilter(this.appliedFilter);
    },

    // APPLIQUER LE FILTRE DE DATE (DATES VÉRIFIÉES AVANT L'APPEL)
    applyFilter() {
      if (!this.selectedCompany) {
        this.showError(this.$t("dashboard.companyRequired"));
        return;
      }
      if (!this.canApplyFilter) {
        this.showError(this.dateSelected === "period" ? this.$t("dashboard.periodicDatesRequired") : this.$t("dashboard.dateRequired"));
        return;
      }
      if (this.dateSelected === "period" && this.dateDebut > this.dateFin) {
        this.showError("La date de début doit précéder la date de fin.");
        return;
      }
      this.runFilter({
        type: this.dateSelected,
        dateDebut: this.dateDebut,
        dateFin: this.dateFin,
        datePrecise: this.datePrecise,
      });
    },

    async runFilter(filter) {
      const id = ++this.filterRequest;
      const isPeriod = filter.type === "period";
      const payload = isPeriod
        ? this.buildPayload({ dateDebut: this.formatDate(filter.dateDebut), dateFin: this.formatDate(filter.dateFin) })
        : this.buildPayload({ dateGiven: this.formatDate(filter.datePrecise) });

      this.filtering = true;
      const [stats, bags] = await this.fetchPeriod(isPeriod ? "period" : "date", payload);
      if (id !== this.filterRequest) return;
      this.filtering = false;

      this.filteredStats = stats;
      this.filteredBags = bags;
      this.appliedFilter = stats || bags ? filter : null;
    },

    clearFilter() {
      this.filterRequest++;
      this.filtering = false;
      this.appliedFilter = null;
      this.filteredStats = null;
      this.filteredBags = null;
      this.dateSelected = null;
    },

    // STATISTIQUES ANNUELLES D'UNE OFFRE DE VOYAGE
    async getOfferStatistics(designation) {
      this.selectedOfferDesignation = designation;
      this.offerStats = null;
      this.loadingOfferStats = true;
      const item = await this.post(API_STATISTIQUE_OFFRE_VOYAGE_RESERVATION("annuel"), {
        data: {
          offreVoyageDesignation: designation,
          raisonSociale: this.selectedCompany,
          dateGiven: null,
          period: { dateDebut: null, dateFin: null },
        },
      });
      this.loadingOfferStats = false;
      if (designation === this.selectedOfferDesignation) this.offerStats = item;
    },

    clearOfferSelection() {
      this.selectedOfferDesignation = null;
      this.offerStats = null;
    },

    // FORMAT DE LA DATE (AAAA-MM-JJ -> JJ/MM/AAAA)
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },
  },

  mounted() {
    this.loadCompanies();
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

/* EN-TÊTE */
.adm-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 28px;
}

.adm-head__date {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $ov-green;
}

.adm-head__title {
  margin: 4px 0 2px;
  font-size: 24px;
  font-weight: 900;
  letter-spacing: -0.4px;
  color: $ov-navy;
}

.adm-head__sub {
  font-size: 13px;
  color: $ov-muted;
}

.adm-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;

  .ov-select,
  .ov-input {
    width: auto;
    min-width: 170px;
    height: 40px;
    background-color: #ffffff;
  }

  .ov-select:disabled {
    background-color: $ov-soft;
  }

  .ov-btn {
    height: 40px;
  }
}

.adm-start {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 48px 24px;
  text-align: center;

  p {
    margin: 0;
    max-width: 420px;
  }
}

/* SECTIONS */
.adm-section {
  margin-bottom: 40px;
}

.adm-section__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;

  &--filters {
    flex-wrap: wrap;
    justify-content: space-between;
  }
}

.adm-section__titles {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.adm-section__title {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: $ov-navy;
}

.adm-block {
  margin-bottom: 28px;
}

.adm-block__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.adm-block__title {
  margin: 0;
  font-size: 14px;
  font-weight: 800;
  color: $ov-muted;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.adm-block__close {
  margin-left: auto;
}

/* OFFRES */
.adm-seg {
  display: inline-flex;
  padding: 3px;
  background: #f1f5f9;
  border-radius: 10px;
}

.adm-seg__opt {
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  color: $ov-muted;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &--active {
    background: #ffffff;
    color: $ov-navy;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
  }
}

.adm-offer-name {
  font-weight: 700;
  color: $ov-navy;

  &--selected {
    color: $ov-green-dark;
  }
}

.adm-actions {
  justify-content: flex-end;
}

.adm-offer {
  margin-top: 20px;
  padding: 20px;
  border: 1px solid rgba(21, 158, 114, 0.3);
  border-radius: 16px;
  background: rgba(21, 158, 114, 0.03);
}

.adm-offer__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.adm-offer__title {
  font-size: 18px;
  font-weight: 800;
  color: $ov-navy;
}

@media (max-width: 760px) {
  .adm-filters {
    width: 100%;

    .ov-select,
    .ov-input,
    .ov-btn {
      flex: 1;
      min-width: 0;
    }
  }
}
</style>
